/**
 * Centralized CO2 calculation utilities
 */

/**
 * Calculate CO2 emissions for standard paint applications
 * Formula: (quantity / coverage) * carbon * coats
 *
 * @param {number} quantity - The quantity of area/length to be painted
 * @param {number} coverage - The coverage rate of the paint
 * @param {number} carbon - The carbon footprint per unit
 * @param {number} coats - The number of coats to be applied (default: 1)
 * @returns {number} The calculated CO2 emissions
 */
export const calculateStandardCO2 = (quantity, coverage, carbon, coats = 1) => {
  // Validate inputs
  if (!quantity || !coverage || !carbon || quantity <= 0 || coverage <= 0 || carbon <= 0) {
    return 0;
  }
  coverage = 1;

  const co2Used = (quantity / coverage) * carbon * coats;
  console.log(`CO2 calculation (standard): (${quantity} / ${coverage}) * ${carbon} * ${coats} = ${co2Used}`);

  return co2Used;
};

/**
 * Calculate CO2 emissions for linear meter applications with paint coverage adjustment
 * Formula: quantity / (coverage / paintCoverage) * carbon * coats
 *
 * @param {number} quantity - The quantity of linear meters
 * @param {number} coverage - The coverage rate
 * @param {number} paintCoverage - The paint coverage adjustment factor
 * @param {number} carbon - The carbon footprint per unit
 * @param {number} coats - The number of coats to be applied (default: 1)
 * @returns {number} The calculated CO2 emissions
 */
export const calculateLinearMeterCO2 = (quantity, coverage, paintCoverage, carbon, coats = 1) => {
  // Validate inputs
  if (!quantity || !coverage || !paintCoverage || !carbon ||
      quantity <= 0 || coverage <= 0 || paintCoverage <= 0 || carbon <= 0) {
    return 0;
  }

  const co2Used = quantity / (coverage / paintCoverage) * carbon * coats;
  console.log(`CO2 calculation (linear meters): ${quantity} / (${coverage} / ${paintCoverage}) * ${carbon} * ${coats} = ${co2Used}`);

  return co2Used;
};

/**
 * Calculate CO2 emissions for m2 applications (coverage always set to 1)
 * Formula: quantity * carbon * coats
 *
 * @param {number} quantity - The quantity in square meters
 * @param {number} carbon - The carbon footprint per unit
 * @param {number} coats - The number of coats to be applied (default: 1)
 * @returns {number} The calculated CO2 emissions
 */
export const calculateM2CO2 = (quantity, carbon, coats = 1) => {
  // Validate inputs
  if (!quantity || !carbon || quantity <= 0 || carbon <= 0) {
    return 0;
  }

  // For m2 calculations, coverage is always 1
  const coverage = 1;
  const co2Used = (quantity / coverage) * carbon * coats;
  console.log(`CO2 calculation (m2 - coverage fixed to 1): (${quantity} / ${coverage}) * ${carbon} * ${coats} = ${co2Used}`);

  return co2Used;
};
